// src/components/staffing/CandidateTile.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Tooltip,
  Avatar,
  LinearProgress,
  CircularProgress,
  Divider
} from '@mui/material';
import Button from '@mui/material/Button';
import {
  CheckCircleOutline as CheckIcon,
  WarningAmber as WarnIcon,
  CalendarToday as CalIcon,
  TrendingUp as TrendIcon,
  AssignmentInd as AssignIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { COLOR_GOOD, COLOR_WARN, COLOR_POOR, TEXT_DISABLED, getScoreColor, chipStyle } from '../../utils/colors';
import AvailabilityChip from '../common/AvailabilityChip';
import MetricCopyright from '../common/MetricCopyright';

const RISK_COLOURS = {
  green:  '#03DAC6',
  yellow: '#FFD740',
  orange: '#FF9800'
};

const RISK_LABELS = {
  green:  'AVAIL: LOW RISK',
  yellow: 'AVAIL: SOME RISK',
  orange: 'AVAIL: HIGH RISK'
};

const localGetScoreColor = (score) => {
  if (score == null) return TEXT_DISABLED;
  const n = parseFloat(score);
  if (n > 6.5) return COLOR_GOOD;
  if (n >= 4.5) return COLOR_WARN;
  return COLOR_POOR;
};

// Soft booking row — one entry from availability_facts.soft_bookings
const SoftBookingRow = ({ booking }) => {
  const prob = booking.win_probability;          // null if no pipeline match
  const probDisplay = prob != null ? prob : '?';
  const isHighRisk = prob != null && prob >= 60;
  const isMidRisk  = prob != null && prob >= 30;
  const isUnknown  = prob == null;
  const colour   = isHighRisk ? '#FF9800' : isMidRisk ? '#FFD740' : isUnknown ? 'rgba(255,255,255,0.4)' : '#03DAC6';
  const bgColour = isHighRisk ? 'rgba(255,152,0,0.09)' : isMidRisk ? 'rgba(255,215,64,0.08)' : 'rgba(255,255,255,0.04)';

  // Format date range — strip time component if present
  const fmt = (d) => d ? d.split('T')[0] : null;
  const start = fmt(booking.start_date);
  const end   = fmt(booking.end_date);
  const dateRange = start && end ? `${start} → ${end}` : start ? `from ${start}` : end ? `until ${end}` : null;

  return (
    <Box sx={{
      p: '6px 8px', borderRadius: 1,
      bgcolor: bgColour,
      border: `1px solid ${colour}44`,
      mb: 0.5
    }}>
      {/* Top row: icon + client/project + probability */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
        <WarnIcon sx={{ fontSize: '0.72rem', color: colour, mt: '2px', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{
            fontSize: '0.68rem', fontWeight: 600, color: colour,
            display: 'block', lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {booking.project || booking.client || 'Soft booking'}
            {booking.project && booking.client && booking.project !== booking.client
              ? ` · ${booking.client}` : ''}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.61rem', color: 'text.disabled', lineHeight: 1.3 }}>
            {booking.status && <span>{booking.status}{dateRange ? ' · ' : ''}</span>}
            {dateRange && <span>{dateRange}</span>}
          </Typography>
        </Box>
        {/* Probability badge */}
        <Box sx={{
          px: 0.75, py: 0.15, borderRadius: 0.75, flexShrink: 0,
          bgcolor: `${colour}22`, border: `1px solid ${colour}66`
        }}>
          <Typography variant="caption" sx={{ fontSize: '0.62rem', fontWeight: 700, color: colour }}>
            {probDisplay}% close
          </Typography>
        </Box>
      </Box>
      {/* Probability bar */}
      {prob != null && (
        <LinearProgress
          variant="determinate"
          value={prob}
          sx={{
            mt: 0.75, height: 3, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: colour, borderRadius: 2 }
          }}
        />
      )}
    </Box>
  );
};

const buildAvailChip = (engEnds, softBookings) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const engEndsDate = engEnds ? new Date(engEnds + 'T00:00:00') : null;
  const isNow = !engEndsDate || engEndsDate <= today;
  const label = isNow
    ? 'Available Now'
    : `Available ${new Date(engEnds + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  const softEnd = (softBookings || []).reduce((latest, sb) => {
    if (!sb.end_date) return latest;
    return !latest || sb.end_date > latest ? sb.end_date : latest;
  }, null);
  const softEndsDate = softEnd ? new Date(softEnd + 'T00:00:00') : null;
  const showSoft = softEndsDate && softEndsDate > today && (!engEndsDate || softEndsDate > engEndsDate);
  return { label, softEnd: showSoft ? softEnd : null };
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 0.5 }}
  >
    {children}
  </Typography>
);

const CandidateTile = React.forwardRef(({
  candidate,
  onViewDetails,
  onAssign = null,
  onUnassign = null,
  isAssignedToRole = false,
  availabilityLabel = null,
  riskLevel = 'green',
  isAssignedOnProject = false,
  techFitOpen: techFitOpenProp = undefined,
  onTechFitToggle = undefined,
  ...cardProps
}, ref) => {

  const firstName   = candidate.first_name || '';
  const lastName    = candidate.last_name  || '';
  const displayName = `${firstName} ${lastName}`.trim() || candidate.employee_name || 'Unknown';
  const initials    = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';

  const [avatarError, setAvatarError] = useState(false);
  const [techFitOpenLocal, setTechFitOpenLocal] = useState(false);
  // Use controlled prop when provided, otherwise fall back to local state
  const techFitOpen = techFitOpenProp !== undefined ? techFitOpenProp : techFitOpenLocal;
  const setTechFitOpen = onTechFitToggle !== undefined ? onTechFitToggle : setTechFitOpenLocal;
  const avatarSrc = !avatarError && firstName && lastName
    ? `/avatars/${firstName.toLowerCase()}_${lastName.toLowerCase()}_avatar.png`
    : undefined;

  const techScore   = candidate.overall_technical_score;
  const discDisplay = candidate.disc_profile || candidate.disc_type || null;
  const yearsExp    = candidate.years_experience;
  const costRate    = candidate.cost_rate || candidate.employee_cost_rate || null;
  const dirScore    = candidate.dir_score ?? candidate.dir_2_0 ?? null;
  const csatScore   = candidate.csat ?? null;
  const location    = candidate.location;

  const topSkills      = (candidate.top_skills || []).slice(0, 5);
  const skillsBreakdown = candidate.skills_breakdown || [];

  const pillColour  = RISK_COLOURS[riskLevel] || RISK_COLOURS.green;
  const riskLabel   = RISK_LABELS[riskLevel]  || RISK_LABELS.green;
  const availLabel  = availabilityLabel || candidate.availability_label || candidate.availability_reason || 'Available';

  // Selection reasons — array of bullets from Claude
  const selectionReasons = Array.isArray(candidate.selection_reasons) && candidate.selection_reasons.length > 0
    ? candidate.selection_reasons
    : [];

  // Prose paragraph from Claude
  const selectionProse = candidate.selection_prose || '';

  // Availability facts
  const avFacts        = candidate.availability_facts || {};
  const csot4          = avFacts.csot_4_weeks;
  const csot8          = avFacts.csot_8_weeks;
  const engEnds        = avFacts.current_engagement_ends;
  const overlapDays    = avFacts.overlap_days ?? 0;  // negative = ends before start
  const currentProject = avFacts.current_project || null;
  const softBookings   = avFacts.soft_bookings || [];

  // Current availability % (inverse of CSOT)
  const currentAvailPct = csot4 != null ? Math.max(0, Math.min(100, Math.round(100 - csot4))) : null;
  // Rising CSOT trend flag
  const risingTrend = csot4 != null && csot8 != null && (csot8 - csot4) >= 10;

  return (
    <Card
      ref={ref}
      {...cardProps}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        border: isAssignedOnProject
          ? '2px solid #7C3AED'
          : '1px solid rgba(255,255,255,0.1)',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 8px 28px rgba(0,0,0,0.25), 0 0 0 1px ${pillColour}33`
        },
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        // Coloured left accent bar matching risk level
        borderLeft: `3px solid ${pillColour}`
      }}
    >
      <CardContent sx={{ p: 2, pb: '16px !important', display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* ── HEADER ── avatar + name/title + risk badge + drag ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
          <Avatar
            src={avatarSrc}
            onError={() => setAvatarError(true)}
            sx={{
              width: 48, height: 48, flexShrink: 0,
              bgcolor: 'primary.dark',
              fontSize: '1rem', fontWeight: 700,
              border: '2px solid rgba(255,255,255,0.1)'
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              onClick={(e) => { e.stopPropagation(); onViewDetails(candidate); }}
              sx={{
                fontWeight: 700, fontSize: '0.9rem',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                cursor: 'pointer', lineHeight: 1.3,
                '&:hover': { color: 'primary.main', textDecoration: 'underline' }
              }}
            >
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', display: 'block', lineHeight: 1.4 }}>
              {candidate.title || ''}
            </Typography>
            {location && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.3 }}>
                {location}
              </Typography>
            )}
          </Box>

          {/* Risk badge */}
          <Chip
            label={riskLabel}
            size="small"
            sx={{
              height: 20, fontSize: '0.58rem', fontWeight: 700,
              letterSpacing: '0.05em',
              bgcolor: `${pillColour}22`,
              color: pillColour,
              border: `1px solid ${pillColour}66`,
              flexShrink: 0
            }}
          />

        </Box>

        {/* ── INSIGHTS ── prose + bullets from Claude ── */}
        {candidate.narrativeLoading ? (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={11} sx={{ color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', fontStyle: 'italic' }}>
              Generating insights...
            </Typography>
          </Box>
        ) : (selectionProse || selectionReasons.length > 0) && (
          <Box sx={{ mb: 1.5 }}>
            <SectionLabel>Insights</SectionLabel>
            {/* Prose paragraph */}
            {selectionProse && (
              <Typography variant="caption" sx={{
                fontSize: '0.7rem', lineHeight: 1.55,
                color: 'text.secondary', display: 'block', mb: selectionReasons.length > 0 ? 1 : 0
              }}>
                {selectionProse}
              </Typography>
            )}
            {/* Bullet highlights */}
            {selectionReasons.map((reason, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.5, alignItems: 'flex-start' }}>
                <CheckIcon sx={{ fontSize: '0.68rem', color: 'primary.main', flexShrink: 0, mt: '3px' }} />
                <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1.4, color: 'text.primary' }}>
                  {reason}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 1.25 }} />

        {/* ── TECHNICAL FIT ── score + disc + exp + skills ── */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: skillsBreakdown.length > 0 ? 'pointer' : 'default' }}
              onClick={skillsBreakdown.length > 0 ? () => setTechFitOpen(!techFitOpen) : undefined}
            >
              <SectionLabel>Technical Fit</SectionLabel>
              {skillsBreakdown.length > 0 && (
                techFitOpen
                  ? <ExpandLessIcon sx={{ fontSize: '0.8rem', color: 'text.disabled', ml: 0.25, mb: 0.5 }} />
                  : <ExpandMoreIcon sx={{ fontSize: '0.8rem', color: 'text.disabled', ml: 0.25, mb: 0.5 }} />
              )}
            </Box>
            {/* Compact metrics row — coloured values, no chip backgrounds */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Tooltip title="Technical skill match (0–10)">
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: localGetScoreColor(techScore), lineHeight: 1 }}>
                    {techScore != null ? parseFloat(techScore).toFixed(1) : '—'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', letterSpacing: '0.05em' }}>SKILL</Typography>
                </Box>
              </Tooltip>
              {dirScore != null && (
                <Tooltip title="Delivery Impact Rating (0–5)">
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', lineHeight: 1, color: dirScore > 0 ? COLOR_GOOD : dirScore === 0 ? COLOR_WARN : COLOR_POOR }}>
                      {parseFloat(dirScore).toFixed(1)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', letterSpacing: '0.05em' }}><MetricCopyright name="DIR" /></Typography>
                  </Box>
                </Tooltip>
              )}
              {csatScore != null && (
                <Tooltip title="Client Satisfaction Score (0–5)">
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', lineHeight: 1, color: csatScore >= 3.5 ? COLOR_GOOD : csatScore >= 2.5 ? COLOR_WARN : COLOR_POOR }}>
                      {parseFloat(csatScore).toFixed(1)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', letterSpacing: '0.05em' }}>CSAT</Typography>
                  </Box>
                </Tooltip>
              )}
              {yearsExp != null && (
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                  {yearsExp}y
                </Typography>
              )}
              {discDisplay && (
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {discDisplay}
                </Typography>
              )}
            </Box>
          </Box>
          {topSkills.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {topSkills.map((skill, idx) => (
                <Chip
                  key={idx}
                  label={skill}
                  size="small"
                  sx={{ height: 19, fontSize: '0.62rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'text.secondary' }}
                />
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label={costRate ? `Cost $${typeof costRate === 'number' ? costRate.toFixed(0) : costRate}/hr` : 'Cost N/A'}
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'text.disabled' }}
            />
          </Box>

          {/* ── Collapsible skill-by-skill breakdown ── */}
          {techFitOpen && skillsBreakdown.length > 0 && (() => {
            const total = candidate.skills_breadth_total || skillsBreakdown.length || 1;
            const directCount = skillsBreakdown.filter(s => (s.weighted_score ?? 0) > 0 && (s.match_type === 'direct' || s.match_type === 'equivalent')).length;
            const relatedCount = skillsBreakdown.filter(s => (s.weighted_score ?? 0) > 0 && s.match_type === 'related').length;
            const gapCount = Math.max(0, total - directCount - relatedCount);
            const directPct = (directCount / total) * 100;
            const relatedPct = (relatedCount / total) * 100;
            const gapPct = (gapCount / total) * 100;
            return (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                {/* Segmented breadth bar */}
                <Box sx={{ display: 'flex', height: 5, borderRadius: 2, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.08)', mb: 0.4 }}>
                  {directPct > 0 && <Box sx={{ width: `${directPct}%`, bgcolor: COLOR_GOOD, transition: 'width 0.3s' }} />}
                  {relatedPct > 0 && <Box sx={{ width: `${relatedPct}%`, bgcolor: COLOR_WARN, transition: 'width 0.3s' }} />}
                  {gapPct > 0 && <Box sx={{ width: `${gapPct}%`, bgcolor: COLOR_POOR, transition: 'width 0.3s' }} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 0.75 }}>
                  {directCount > 0 && <Typography sx={{ fontSize: '0.6rem', color: COLOR_GOOD }}>{directCount} direct</Typography>}
                  {relatedCount > 0 && <Typography sx={{ fontSize: '0.6rem', color: COLOR_WARN }}>{relatedCount} related</Typography>}
                  {gapCount > 0 && <Typography sx={{ fontSize: '0.6rem', color: COLOR_POOR }}>{gapCount} gap{gapCount > 1 ? 's' : ''}</Typography>}
                </Box>
                {/* Per-skill rows in requirements order */}
                {skillsBreakdown.map((skill, skillIndex) => {
                  const score = skill.weighted_score ?? skill.proficiency_score ?? 0;
                  const scoreColor = score === 0 ? COLOR_POOR : getScoreColor(score);
                  const isDirect = skill.match_type === 'direct';
                  const isEquivalent = skill.match_type === 'equivalent';
                  const isRelated = skill.match_type === 'related';
                  const skillDiffers = skill.employee_skill_name && skill.employee_skill_name.toLowerCase() !== skill.required_skill.toLowerCase();
                  const matchColor = isDirect || isEquivalent ? COLOR_GOOD : isRelated ? COLOR_WARN : COLOR_POOR;
                  const matchLabel = isDirect ? 'DIRECT' : isEquivalent ? 'EQUIV' : isRelated ? 'RELATED' : 'GAP';
                  return (
                    <Box
                      key={skillIndex}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 0.35,
                        borderBottom: skillIndex < skillsBreakdown.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                        opacity: score === 0 ? 0.65 : 1,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: scoreColor, minWidth: '22px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {score === 0 ? '—' : score.toFixed(1)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', flexGrow: 1, color: score === 0 ? COLOR_POOR : 'text.primary', textDecoration: score === 0 ? 'line-through' : 'none', lineHeight: 1.3 }}>
                        {skill.required_skill}
                      </Typography>
                      {skillDiffers && score > 0 && (
                        <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                          via {skill.employee_skill_name}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 'bold', color: matchColor, border: `1px solid ${matchColor}40`, borderRadius: '3px', px: 0.4, py: 0.1, whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                        {matchLabel}
                      </Typography>
                      {skill.reasoning && (
                        <Tooltip title={skill.reasoning} arrow placement="left" enterDelay={300}>
                          <InfoIcon sx={{ fontSize: 11, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </Box>

        <Divider sx={{ mb: 1.25 }} />

        {/* ── AVAILABILITY ── bars + soft bookings + engagement ── */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <SectionLabel>Availability</SectionLabel>
            <AvailabilityChip
              {...buildAvailChip(engEnds, softBookings)}
              tooltip={candidate.availability_detail}
            />
          </Box>

          {/* Current availability bar */}
          {currentAvailPct != null && (
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                  Current Capacity
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                  {currentAvailPct}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={currentAvailPct}
                sx={{
                  height: 5, borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: currentAvailPct >= 75 ? COLOR_GOOD : currentAvailPct >= 40 ? COLOR_WARN : COLOR_POOR,
                    borderRadius: 3
                  }
                }}
              />
            </Box>
          )}

          {/* Rising CSOT trend warning */}
          {risingTrend && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.75 }}>
              <TrendIcon sx={{ fontSize: '0.7rem', color: '#FFD740' }} />
              <Typography variant="caption" sx={{ fontSize: '0.63rem', color: '#FFD740' }}>
                Utilization rising — {csot4}% now → {csot8}% projected
              </Typography>
            </Box>
          )}

          {/* Current engagement — ends before role start (overlap_days < 0) */}
          {engEnds && overlapDays < 0 && (
            <Box sx={{
              display: 'flex', gap: 0.6, alignItems: 'flex-start', mb: 0.75,
              p: '5px 8px', borderRadius: 1,
              bgcolor: 'rgba(3,218,198,0.07)', border: '1px solid rgba(3,218,198,0.25)'
            }}>
              <CalIcon sx={{ fontSize: '0.7rem', color: COLOR_GOOD, mt: '2px', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: '0.63rem', color: 'text.secondary', lineHeight: 1.4 }}>
                {currentProject
                  ? <>Currently on <strong>{currentProject}</strong>, finishing <strong>{engEnds}</strong> — clear {Math.abs(overlapDays)}d before role start</>
                  : <>Current engagement ends <strong>{engEnds}</strong> — free {Math.abs(overlapDays)}d before role start</>
                }
              </Typography>
            </Box>
          )}

          {/* Current engagement — overlaps role start (overlap_days > 0) */}
          {engEnds && overlapDays > 0 && (
            <Box sx={{
              display: 'flex', gap: 0.6, alignItems: 'flex-start', mb: 0.75,
              p: '5px 8px', borderRadius: 1,
              bgcolor: overlapDays > 14 ? 'rgba(255,152,0,0.08)' : 'rgba(255,215,64,0.07)',
              border: `1px solid ${overlapDays > 14 ? 'rgba(255,152,0,0.3)' : 'rgba(255,215,64,0.3)'}`
            }}>
              <CalIcon sx={{ fontSize: '0.7rem', color: overlapDays > 14 ? '#FF9800' : '#FFD740', mt: '2px', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: '0.63rem', color: 'text.secondary', lineHeight: 1.4 }}>
                {currentProject
                  ? <>On <strong>{currentProject}</strong> until <strong>{engEnds}</strong> — <strong>{overlapDays}d overlap</strong> with role start</>
                  : <>Engagement ends <strong>{engEnds}</strong> — <strong>{overlapDays}d overlap</strong> with role start</>
                }
              </Typography>
            </Box>
          )}

          {/* Soft bookings */}
          {softBookings.length > 0 && (
            <Box sx={{ mt: 0.25 }}>
              {softBookings.map((sb, i) => (
                <SoftBookingRow key={i} booking={sb} />
              ))}
            </Box>
          )}

          {/* Fallback: availability detail when no structured data */}
          {candidate.availability_detail && softBookings.length === 0 && !engEnds && currentAvailPct == null && (
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
              {candidate.availability_detail}
            </Typography>
          )}
        </Box>

        {/* ── ASSIGN / UNASSIGN BUTTON ── */}
        {(onAssign || onUnassign) && (
          <Box sx={{ mt: 1.5 }}>
            {isAssignedToRole ? (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={(e) => { e.stopPropagation(); onUnassign && onUnassign(); }}
                sx={{
                  fontSize: '0.72rem', fontWeight: 600, py: 0.6,
                  borderColor: 'rgba(255,255,255,0.2)', color: 'text.secondary',
                  '&:hover': { borderColor: '#FF5252', color: '#FF5252', bgcolor: 'rgba(255,82,82,0.08)' }
                }}
              >
                Unassign
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<AssignIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={(e) => { e.stopPropagation(); onAssign && onAssign(candidate); }}
                sx={{
                  fontSize: '0.72rem', fontWeight: 700, py: 0.6,
                  bgcolor: pillColour, color: '#000',
                  '&:hover': { bgcolor: pillColour, filter: 'brightness(1.15)' }
                }}
              >
                Assign to Role
              </Button>
            )}
          </Box>
        )}

      </CardContent>
    </Card>
  );
});

CandidateTile.displayName = 'CandidateTile';

CandidateTile.propTypes = {
  candidate: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onAssign: PropTypes.func,
  onUnassign: PropTypes.func,
  isAssignedToRole: PropTypes.bool,
  availabilityLabel: PropTypes.string,
  riskLevel: PropTypes.oneOf(['green', 'yellow', 'orange']),
  isAssignedOnProject: PropTypes.bool,
  // Legacy props — accepted but not rendered
  isRecommended: PropTypes.bool,
  overallRank: PropTypes.number,
  compositeScore: PropTypes.number,
  recommendationReasons: PropTypes.arrayOf(PropTypes.string),
  onAnalyzeChemistry: PropTypes.func
};

export default CandidateTile;
